import { call, put, takeLatest, ForkEffect } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { actions } from "reducers/governmentalEntity";
import { actions as notificationActions } from "reducers/notification";
import {
  getVersions,
  uploadVersion,
  GetVersionsResponseType,
} from "middleware/governmentalEntity";
import {
  GetVersionsPayloadType,
  UploadVersionPayloadType,
} from "./types/governmentalEntity";
import i18next from "config/i18next";

const isAxiosError = <T>(error: AxiosError<T>): error is AxiosError<T> =>
  error.isAxiosError === true;

const parseErrorMessage = (error: AxiosError<{ message?: string | string[] }>): string => {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join(", ");
  if (typeof message === "string") return message;
  return "Error al procesar la solicitud";
};

function* getVersionsSaga({
  payload,
}: PayloadAction<GetVersionsPayloadType>) {
  try {
    yield put(actions.getVersionsRequest());

    const response: AxiosResponse<GetVersionsResponseType> = yield call(
      getVersions,
      payload?.limit ?? 10
    );

    const versionsData = response.data?.result;
    yield put(
      actions.getVersionsSuccess(Array.isArray(versionsData) ? versionsData : [])
    );
  } catch (error) {
    if (isAxiosError(error as AxiosError<{ message?: string | string[] }>)) {
      if ((error as AxiosError<{ message?: string | string[] }>)?.response?.status === 401) throw error;
      yield put(actions.getVersionsError(parseErrorMessage(error as AxiosError<{ message?: string | string[] }>)));
    }
  }
}

function* uploadVersionSaga({
  payload,
}: PayloadAction<UploadVersionPayloadType>) {
  try {
    yield put(actions.uploadVersionRequest());

    const uploadResponse: AxiosResponse<{ statusCode?: number, errors?: string[] }> = yield call(
      uploadVersion,
      payload.file
    );

    if (uploadResponse?.data?.statusCode === 30070) {
      yield put(actions.uploadVersionError(""));
      yield put(
        notificationActions.showNotification({
          id: "notification-error-duplicate-version",
          level: "error",
          title: i18next.t("governmentalEntity:errorTitle"),
            body: (uploadResponse.data.errors?.length ?? 0) > 1 ? i18next.t("governmentalEntity:errorBody", { missingData: uploadResponse.data.errors?.join(", ") ?? "" }) : i18next.t("governmentalEntity:errorBodySingle", { missingData: uploadResponse.data.errors?.[0] ?? "" }),
          onConfirm: () => {},
          onCancel: () => {},
          onClose: () => {},
        })
      );
      return;
    }

    yield put(actions.uploadVersionSuccess(null));

    const reloadResponse: AxiosResponse<GetVersionsResponseType> = yield call(
      getVersions,
      10
    );

    const versionsData = reloadResponse.data?.result;
    if (Array.isArray(versionsData)) {
      yield put(actions.setVersions(versionsData));
    }
  } catch (error) {
    if (isAxiosError(error as AxiosError<{ message?: string | string[] }>)) {
      if ((error as AxiosError<{ message?: string | string[] }>)?.response?.status === 401) throw error;
      yield put(actions.uploadVersionError(parseErrorMessage(error as AxiosError<{ message?: string | string[] }>)));
    }
  }
}

const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getVersions.type, getVersionsSaga),
  takeLatest(actions.uploadVersion.type, uploadVersionSaga),
];

export default sagas;
